import React, { useState, useEffect } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import { API, fmt$, fmtN, fmtPct } from "../lib/appCommon";
import { fastGetAsync } from "../lib/fastFetch";
import PerformanceDashboard from "./PerformanceDashboard";
import TradeHistoryCard from "./TradeHistoryCard";
import OmegaEnhancementsCard from "./OmegaEnhancementsCard";
import "./AnalyticsTab.css";

// ─── Icons ──────────────────────────────────────────────────────────────────
const ICON_PATHS = {
  chart: <><path d="M3 3v18h18"/><path d="M18.7 8 13 13.7l-4-4L3 16"/></>,
  target: <><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1"/></>,
  list: <><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></>,
  layers: <><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></>,
  bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></>,
  sparkle: <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.8 2.8M14.9 14.9l2.8 2.8M17.7 6.3l-2.8 2.8M9.1 14.9l-2.8 2.8"/>,
};
function Icon({ name }) {
  const p = ICON_PATHS[name];
  if (!p) return null;
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{p}</svg>;
}

const SECTION_ACCENT = {
  performance: { accent: "var(--blue)",   bg: "rgba(96,165,250,0.13)" },
  signals:     { accent: "var(--purple)", bg: "rgba(167,139,250,0.13)" },
  tradelog:    { accent: "var(--green)",  bg: "rgba(74,222,128,0.13)" },
  strategy:    { accent: "var(--an-gold)", bg: "var(--an-gold-bg)" },
  alerts:      { accent: "#f87171",       bg: "rgba(248,113,113,0.13)" },
  enhance:     { accent: "var(--an-teal)", bg: "var(--an-teal-bg)" },
};

// Old flat sub-tab ids (from links/dispatches elsewhere in the app) mapped to
// the new merged section + its internal sub-view.
const LEGACY_SUBTAB_MAP = {
  performance: ["performance", "overview"], attribution: ["performance", "vectors"], perfAttrib: ["performance", "pnl"],
  leaderboard: ["signals", "leaderboard"], confidence: ["signals", "confidence"],
  journal: ["tradelog", "journal"], history: ["tradelog", "history"],
  optimizer: ["strategy", "optimizer"], backtest: ["strategy", "backtest"],
  alerts: ["alerts", null], enhance: ["enhance", null],
};

function RingGauge({ pct, size = 76, stroke = 8, color }) {
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - clamped / 100);
  return (
    <div className="an-gauge" style={{ width: size, height: size, position: "relative" }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--layer2)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.22, fontWeight: 800, color }}>
        {clamped.toFixed(0)}%
      </div>
    </div>
  );
}

function DualBar({ winPct, winLabel, lossLabel }) {
  const w = Math.max(0, Math.min(100, winPct));
  return (
    <div className="an-dual-bar">
      <div className="win" style={{ width: `${w}%` }}>{winLabel}</div>
      <div className="loss" style={{ width: `${100 - w}%` }}>{lossLabel}</div>
    </div>
  );
}

// ─── Section shell ──────────────────────────────────────────────────────────
function Section({ id, activeId, title, icon, sub, children }) {
  if (id !== activeId) return null;
  return (
    <div className="an-section" style={{ "--accent": SECTION_ACCENT[id].accent, "--accent-bg": SECTION_ACCENT[id].bg }} data-testid={`an-section-${id}`}>
      <div className="an-section-head">
        <div>
          <div className="an-section-title"><Icon name={icon} />{title}</div>
          {sub && <div className="an-section-sub">{sub}</div>}
        </div>
      </div>
      <div className="an-section-body">{children}</div>
    </div>
  );
}

function SubToggle({ views, active, onChange }) {
  return (
    <div className="an-subtoggle">
      {views.map(([id, label]) => (
        <button key={id} className={active === id ? "on" : ""} onClick={() => onChange(id)}>{label}</button>
      ))}
    </div>
  );
}

// ─── Performance & Attribution ──────────────────────────────────────────────
function PerformanceSection({ view, setView, analytics, priors, apPerf, attribution }) {
  const winByClass = Object.entries(apPerf?.byClass || {});
  return (
    <div>
      <SubToggle views={[["overview", "Overview"], ["vectors", "Vectors"], ["pnl", "P&L Breakdown"]]} active={view} onChange={setView} />

      {view === "overview" && (
        <div>
          <div className="an-stat-grid" style={{ marginBottom: 8 }}>
            <div className="an-stat-chip"><div className="l">Win Rate</div><div className="v" style={{ color: "var(--green)" }}>{analytics?.winRate || 0}%</div></div>
            <div className="an-stat-chip"><div className="l">Avg Win</div><div className="v" style={{ color: "var(--green)" }}>{fmt$(analytics?.avgWin)}</div></div>
            <div className="an-stat-chip"><div className="l">Avg Loss</div><div className="v" style={{ color: "var(--red)" }}>{fmt$(analytics?.avgLoss)}</div></div>
            <div className="an-stat-chip"><div className="l">Sharpe Ratio</div><div className="v">{analytics?.sharpe || 0}</div></div>
          </div>
          <div className="an-stat-grid" style={{ marginBottom: 16 }}>
            <div className="an-stat-chip"><div className="l">Total P&L</div><div className="v" style={{ color: (analytics?.totalPnl || 0) >= 0 ? "var(--green)" : "var(--red)" }}>{fmt$(analytics?.totalPnl)}</div></div>
            <div className="an-stat-chip"><div className="l">Total Trades</div><div className="v">{analytics?.totalTrades || 0}</div></div>
            <div className="an-stat-chip"><div className="l">Expectancy</div><div className="v">{analytics ? fmt$(analytics.avgWin * analytics.winRate / 100 - analytics.avgLoss * (100 - analytics.winRate) / 100) : "—"}</div></div>
            <div className="an-stat-chip"><div className="l">Profit Factor</div><div className="v">{analytics && analytics.avgLoss > 0 && analytics.winRate < 100 ? ((analytics.avgWin * analytics.winRate) / (analytics.avgLoss * (100 - analytics.winRate))).toFixed(2) : "—"}</div></div>
          </div>

          {apPerf && apPerf.totalTrades > 0 && (
            <>
              <div className="an-side-title" style={{ marginBottom: 8 }}>Win Rate by Zi Class</div>
              <div style={{ marginBottom: 16 }}>
                {winByClass.map(([cls, d]) => (
                  <div key={cls} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, color: cls === "ULTRA_GEM" ? "var(--green)" : cls === "GEM" ? "var(--blue)" : "var(--yellow)" }}>{cls}</span>
                      <span style={{ color: "var(--muted)" }}>{d.trades} trades · {fmt$(d.pnl)}</span>
                    </div>
                    <DualBar winPct={d.winRate} winLabel={`${d.wins}W`} lossLabel={`${d.trades - d.wins}L`} />
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="an-side-title" style={{ marginBottom: 8 }}>Bayesian Self-Learning Priors</div>
          <div className="an-table-wrap">
            <table className="tbl">
              <thead><tr><th style={{ textAlign: "left" }}>Setup ID</th><th>Win Prob</th><th>Wins</th><th>Losses</th></tr></thead>
              <tbody>
                {Object.keys(priors).length === 0 && <tr><td colSpan={4} style={{ color: "var(--muted)" }}>No prior data yet. Trade and close positions to build Bayesian analytics.</td></tr>}
                {Object.entries(priors).map(([id, p]) => (
                  <tr key={id}>
                    <td style={{ textAlign: "left", fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>{id}</td>
                    <td>{((p.alpha || 1) / ((p.alpha || 1) + (p.beta || 1)) * 100).toFixed(1)}%</td>
                    <td>{p.alpha ?? p.wins ?? 0}</td><td>{p.beta ?? p.losses ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === "vectors" && (
        <div>
          {attribution && Object.keys(attribution.attribution || {}).length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              {Object.entries(attribution.attribution).map(([key, v]) => {
                const maxVal = Math.max(v.winAvg, v.lossAvg, 1);
                return (
                  <div key={key} style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 650 }}>{v.label}</span>
                      <span style={{ fontSize: 11, color: v.diff > 0 ? "var(--green)" : v.diff < 0 ? "var(--red)" : "var(--muted)", fontWeight: 700 }}>{v.diff > 0 ? "+" : ""}{v.diff} edge</span>
                    </div>
                    <div style={{ display: "flex", gap: 4, height: 20, borderRadius: 6, overflow: "hidden" }}>
                      <div style={{ width: `${(v.winAvg / maxVal) * 100}%`, background: "rgba(74,222,128,0.3)", borderRadius: "6px 0 0 6px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "var(--green)", minWidth: 40 }}>W: {v.winAvg}</div>
                      <div style={{ width: `${(v.lossAvg / maxVal) * 100}%`, background: "rgba(248,113,113,0.3)", borderRadius: "0 6px 6px 0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "var(--red)", minWidth: 40 }}>L: {v.lossAvg}</div>
                    </div>
                    <div style={{ fontSize: 9, color: "var(--dim)", marginTop: 2 }}>{v.winSamples} win samples / {v.lossSamples} loss samples</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ color: "var(--muted)", fontSize: 12, marginBottom: 16 }}>Run autopilot scans to populate attribution data. Each scan feeds into the vector analysis.</div>
          )}

          {attribution && Object.keys(attribution.byClass || {}).length > 0 && (
            <>
              <div className="an-side-title" style={{ marginBottom: 8 }}>Average Vectors by Zi Class</div>
              <div className="an-table-wrap" style={{ marginBottom: 16 }}>
                <table className="tbl">
                  <thead><tr><th style={{ textAlign: "left" }}>Class</th><th>Count</th><th>Avg Zi</th><th>Avg MC PoP</th><th>Avg Tea</th><th>Avg IV</th></tr></thead>
                  <tbody>
                    {Object.entries(attribution.byClass).sort((a, b) => b[1].avgZi - a[1].avgZi).map(([cls, d]) => (
                      <tr key={cls}>
                        <td style={{ textAlign: "left", fontWeight: 650, color: cls === "ULTRA_GEM" ? "var(--green)" : cls === "GEM" ? "var(--blue)" : "var(--yellow)" }}>{cls}</td>
                        <td>{d.count}</td><td style={{ fontWeight: 650 }}>{d.avgZi}</td><td>{d.avgPoP}%</td><td>{d.avgTea}</td><td>{d.avgIV}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="an-stat-grid">
            <div className="an-stat-chip"><div className="l">Total Scanned</div><div className="v">{attribution?.totalScanned || 0}</div></div>
            <div className="an-stat-chip"><div className="l">Avg Zi Score</div><div className="v">{attribution?.avgZiScore || 0}</div></div>
            <div className="an-stat-chip"><div className="l">Avg MC PoP</div><div className="v">{attribution?.avgMcPoP || 0}%</div></div>
          </div>
        </div>
      )}

      {view === "pnl" && <PerformanceDashboard />}
    </div>
  );
}

// ─── Signals (Leaderboard + Confidence) ─────────────────────────────────────
function SignalsSection({ view, setView, leaderboard, lbSort, setLbSort, setLeaderboard, confHistory, confFilter, setConfFilter }) {
  return (
    <div>
      <SubToggle views={[["leaderboard", "Leaderboard"], ["confidence", "Confidence"]]} active={view} onChange={setView} />

      {view === "leaderboard" && (
        leaderboard.length > 0 ? (
          <div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginBottom: 10 }}>
              {["ziScore", "mcPoP", "teaScore", "ivRank"].map(col => (
                <button key={col} className={`an-btn ${lbSort === col ? "primary" : "ghost"}`} style={{ fontSize: 10, padding: "5px 10px" }}
                  onClick={() => { setLbSort(col); setLeaderboard(prev => [...prev].sort((a, b) => b[col] - a[col])); }}
                  data-testid={`lb-sort-${col}`}>
                  {col === "ziScore" ? "Zi" : col === "mcPoP" ? "MC" : col === "teaScore" ? "Tea" : "IV"}
                </button>
              ))}
            </div>
            <div className="an-table-wrap" style={{ maxHeight: 520, overflowY: "auto" }}>
              <table className="tbl" style={{ fontSize: 11 }}>
                <thead><tr>
                  <th style={{ textAlign: "left" }}>#</th><th style={{ textAlign: "left" }}>Ticker</th><th>Strike</th><th>C/P</th><th>Exp</th>
                  <th>Zi Score</th><th>Class</th><th>MC PoP</th><th>Tea</th><th>IV</th><th>DTE</th><th>Mid</th><th>Risk</th>
                </tr></thead>
                <tbody data-testid="leaderboard-rows">
                  {leaderboard.map((r, i) => (
                    <tr key={i} style={{ background: r.ziClass === "ULTRA_GEM" ? "rgba(74,222,128,0.04)" : r.ziClass === "GEM" ? "rgba(96,165,250,0.04)" : "transparent" }}>
                      <td style={{ textAlign: "left", fontWeight: 650, color: i < 3 ? "var(--green)" : "var(--muted)" }}>{i + 1}</td>
                      <td style={{ textAlign: "left", fontWeight: 650 }}>{r.ticker}</td>
                      <td>${r.strike}</td>
                      <td><span style={{ fontSize: 10, fontWeight: 700, color: (r.optionType || "").toLowerCase() === "put" ? "#f87171" : "#4ade80" }}>{(r.optionType || "call").charAt(0).toUpperCase()}</span></td>
                      <td style={{ fontSize: 10 }}>{r.expiry}</td>
                      <td>
                        <div className="an-zi-wrap" style={{ display: "inline-flex" }}>
                          <span className="an-zi-num" style={{ color: r.ziScore >= 68 ? "var(--green)" : r.ziScore >= 53 ? "var(--blue)" : "var(--yellow)" }}>{r.ziScore}</span>
                          <div className="an-zi-bar"><i style={{ width: `${r.ziScore}%`, background: r.ziScore >= 68 ? "var(--green)" : r.ziScore >= 53 ? "var(--blue)" : "var(--yellow)" }} /></div>
                        </div>
                      </td>
                      <td><span className={`tag ${r.ziClass === "ULTRA_GEM" ? "t-bull" : r.ziClass === "GEM" ? "t-blue" : "t-neu"}`} style={{ fontSize: 8 }}>{r.ziClass?.replace("_", " ")}</span></td>
                      <td style={{ color: r.mcPoP > 60 ? "var(--green)" : "var(--muted)" }}>{r.mcPoP}%</td>
                      <td>{r.teaScore ?? "—"}</td>
                      <td>{r.ivRank}</td>
                      <td>{r.dte}</td>
                      <td>${r.mid}</td>
                      <td><span style={{ color: r.riskGatePassed ? "var(--green)" : "var(--red)", fontWeight: 650, fontSize: 10 }}>{r.riskGatePassed ? "PASS" : "BLOCK"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : <div style={{ color: "var(--muted)", fontSize: 12, padding: "20px 0" }}>No leaderboard data yet. Run an autopilot scan from the Options Hub to populate rankings.</div>
      )}

      {view === "confidence" && (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginBottom: 12 }}>
            {[["all", "All"], ["ticker", "Tickers"], ["system", "System"]].map(([v, label]) => (
              <button key={v} className={`an-btn ${confFilter === v ? "primary" : "ghost"}`} style={{ fontSize: 10, padding: "5px 10px" }} onClick={() => setConfFilter(v)}>{label}</button>
            ))}
          </div>
          {confHistory.length > 0 ? (() => {
            const visible = confFilter === "all" ? confHistory : confHistory.filter(h => confFilter === "ticker" ? h.entryType === "ticker" : h.entryType !== "ticker");
            if (visible.length === 0) return <div style={{ color: "var(--muted)", fontSize: 12, padding: "16px 0" }}>No {confFilter} setups yet.</div>;
            return visible.map((h, i) => (
              <div key={i} className="an-card">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{h.entryType === "system" ? h.setupId : h.ticker}</span>
                    {h.entryType === "ticker" && (<>
                      <span className={`tag ${h.regime?.includes("bull") ? "t-bull" : h.regime?.includes("bear") ? "t-bear" : "t-neu"}`} style={{ fontSize: 8 }}>{h.regime?.toUpperCase()}</span>
                      <span className="tag" style={{ fontSize: 8, background: "var(--layer2)" }}>IV: {h.ivBucket}</span>
                    </>)}
                    {h.entryType === "system" && <span className="tag t-pur" style={{ fontSize: 7 }}>SYSTEM</span>}
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 650, color: h.total > 10 ? "var(--green)" : h.total > 3 ? "#facc15" : "var(--dim)" }}>{h.total > 10 ? "HIGH" : h.total > 3 ? "BUILDING" : "LOW"} CONF</span>
                </div>
                <DualBar winPct={h.winRate} winLabel={`${h.wins}W`} lossLabel={`${h.losses}L`} />
                <div style={{ display: "flex", gap: 16, fontSize: 10, color: "var(--muted)", flexWrap: "wrap", marginTop: 8 }}>
                  <span>Win Rate: <span style={{ fontWeight: 650, color: h.winRate > 60 ? "var(--green)" : "var(--muted)" }}>{h.winRate}%</span></span>
                  <span>Tests: <span style={{ fontWeight: 650 }}>{h.total}</span></span>
                  <span>Bayesian: <span style={{ fontWeight: 650, color: h.bayesianWinProb > 60 ? "var(--green)" : h.bayesianWinProb < 40 ? "#f87171" : "var(--muted)" }}>{h.bayesianWinProb}%</span></span>
                </div>
              </div>
            ));
          })() : <div style={{ color: "var(--muted)", fontSize: 12, padding: "20px 0" }}>No confidence data yet. As the AI runs paper trades in the background, confidence metrics will build here automatically.</div>}
        </div>
      )}
    </div>
  );
}

// ─── Trade Log (Journal + Trade History) ────────────────────────────────────
function TradeLogSection({ view, setView, journal, reloadJournal }) {
  const closed = (journal?.journal || []).filter(j => j.status === "closed" && j.pnl != null && j.aiVerdict && j.aiVerdict !== "—" && j.aiVerdict !== "AUTO");
  const verdictPositive = v => /bull|buy|long|strong|positive|up|✓|win/i.test(v);
  const aiMatches = closed.filter(j => verdictPositive(j.aiVerdict) ? j.pnl > 0 : j.pnl <= 0);
  const aiAccuracy = closed.length > 0 ? Math.round(aiMatches.length / closed.length * 100) : null;

  return (
    <div>
      <SubToggle views={[["journal", "Journal"], ["history", "All Trades"]]} active={view} onChange={setView} />

      {view === "journal" && (
        <div className="an-layout">
          <div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
              <button className="an-btn ghost" style={{ fontSize: 10 }} onClick={reloadJournal}>↺ Refresh</button>
            </div>
            {journal?.journal?.length > 0 ? (
              <div className="an-table-wrap" style={{ maxHeight: 460, overflowY: "auto" }}>
                <table className="tbl" style={{ fontSize: 10 }}>
                  <thead><tr>
                    <th style={{ textAlign: "left" }}>Source</th><th style={{ textAlign: "left" }}>Ticker</th><th>Strike</th><th>Side</th>
                    <th>Zi</th><th>AI Verdict</th><th>Entry</th><th>Exit</th><th>P&L</th><th>Status</th><th>Closed</th>
                  </tr></thead>
                  <tbody data-testid="journal-rows">
                    {journal.journal.map((j, i) => (
                      <tr key={i} style={{ background: j.pnl > 0 ? "rgba(74,222,128,0.03)" : j.pnl < 0 ? "rgba(248,113,113,0.03)" : "transparent" }}>
                        <td style={{ textAlign: "left" }}><span className={`tag ${j.source === "autopilot" ? "t-blue" : "t-neu"}`} style={{ fontSize: 8 }}>{j.source}</span></td>
                        <td style={{ textAlign: "left", fontWeight: 650 }}>{j.ticker}</td>
                        <td>${j.strike || "—"}</td>
                        <td><span style={{ color: j.side === "BUY" ? "var(--green)" : "var(--red)" }}>{j.side}</span></td>
                        <td style={{ fontWeight: 650 }}>{j.ziScore || "—"}</td>
                        <td style={{ maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={j.aiVerdict}>{j.aiVerdict || "—"}</td>
                        <td>${fmtN(j.entryPrice, 2)}</td>
                        <td>${fmtN(j.exitPrice, 2)}</td>
                        <td className={j.pnl > 0 ? "pos" : j.pnl < 0 ? "neg" : ""} style={{ fontWeight: 650 }}>{j.pnl ? (j.pnl > 0 ? "+" : "") + fmt$(j.pnl) : "—"}</td>
                        <td><span className={`tag ${j.status === "closed" ? (j.pnl > 0 ? "t-bull" : "t-bear") : "t-neu"}`} style={{ fontSize: 8 }}>{j.status}</span></td>
                        <td style={{ fontSize: 9, color: "var(--dim)" }}>{j.closedAt ? new Date(j.closedAt).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div style={{ color: "var(--muted)", fontSize: 12, padding: 16 }}>No trades recorded yet. Execute trades via the Options Hub to build your journal.</div>}
          </div>
          <div className="an-side">
            {journal?.accuracy && (
              <div className="an-side-card">
                <div className="an-side-title">Accuracy</div>
                <div className="an-gauge-wrap">
                  <RingGauge pct={journal.accuracy.winRate} color={journal.accuracy.winRate >= 50 ? "#4ade80" : "#f87171"} />
                  <div>
                    <div className="an-gauge-sub">Win Rate</div>
                    <div style={{ fontSize: 12, fontWeight: 650, marginTop: 4 }}>{journal.accuracy.totalTrades} trades</div>
                  </div>
                </div>
                <div className="an-side-stat-row"><span className="l">Total P&L</span><span className="v" style={{ color: journal.accuracy.totalPnl >= 0 ? "var(--green)" : "var(--red)" }}>{fmt$(journal.accuracy.totalPnl)}</span></div>
                <div className="an-side-stat-row"><span className="l">Avg P&L</span><span className="v">{fmt$(journal.accuracy.avgPnl)}</span></div>
                <div className="an-side-stat-row"><span className="l">W / L</span><span className="v">{journal.accuracy.wins}W / {journal.accuracy.losses}L</span></div>
              </div>
            )}
            {aiAccuracy != null && (
              <div className="an-insight">
                <span className="tag">AI Prediction Accuracy</span>
                <strong style={{ fontSize: 16 }}>{aiAccuracy}%</strong> ({aiMatches.length}/{closed.length} verdicts matched outcome)
              </div>
            )}
          </div>
        </div>
      )}

      {view === "history" && <TradeHistoryCard />}
    </div>
  );
}

// ─── Strategy Lab (Optimizer + Backtester) ──────────────────────────────────
function StrategyLabSection({
  view, setView,
  optBudget, setOptBudget, optRisk, setOptRisk, optLoading, runOptimizer, optimizer,
  btSymbol, setBtSymbol, btThreshold, setBtThreshold, btHold, setBtHold, btAccountSize, setBtAccountSize,
  btLoading, runBacktest, btResult, btChart,
}) {
  return (
    <div>
      <SubToggle views={[["optimizer", "Optimizer"], ["backtest", "Backtester"]]} active={view} onChange={setView} />

      {view === "optimizer" && (
        <div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 12 }}>Suggests optimal allocation across top-ranked options, balancing risk exposure using Modern Portfolio Theory.</div>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 14 }}>
            <div><div className="m-lbl">Budget ($)</div><input className="inp" type="number" value={optBudget} onChange={e => setOptBudget(Number(e.target.value))} style={{ width: 120 }} data-testid="opt-budget" /></div>
            <div>
              <div className="m-lbl">Risk Tolerance</div>
              <select className="inp" value={optRisk} onChange={e => setOptRisk(e.target.value)} style={{ width: 140 }} data-testid="opt-risk">
                <option value="conservative">Conservative</option>
                <option value="moderate">Moderate</option>
                <option value="aggressive">Aggressive</option>
              </select>
            </div>
            <button className="an-btn primary" onClick={runOptimizer} disabled={optLoading} data-testid="run-optimizer">{optLoading ? "Optimizing..." : "Optimize"}</button>
          </div>
          {optimizer && (
            <div>
              <div className="an-stat-grid" style={{ marginBottom: 12 }}>
                <div className="an-stat-chip"><div className="l">Allocated</div><div className="v">{fmt$(optimizer.totalAllocated)}</div><div className="sub">{optimizer.allocPct}% of budget</div></div>
                <div className="an-stat-chip"><div className="l">Remaining</div><div className="v">{fmt$(optimizer.remaining)}</div></div>
                <div className="an-stat-chip"><div className="l">Positions</div><div className="v">{optimizer.allocations?.length || 0} / {optimizer.maxPositions}</div></div>
                <div className="an-stat-chip"><div className="l">Tier</div><div className="v" style={{ fontSize: 13 }}>{optimizer.tierLabel || "—"}</div><div className="sub">Min Zi {optimizer.minZi}</div></div>
              </div>
              {optimizer.allocations?.length > 0 ? (
                <>
                  <div className="an-side-title" style={{ marginBottom: 8 }}>Allocation Mix</div>
                  <div style={{ marginBottom: 16 }}>
                    {optimizer.allocations.map((a, i) => (
                      <div key={i} className="an-bar-row">
                        <span className="name">{a.ticker} {a.optionType?.charAt(0).toUpperCase()}</span>
                        <div className="an-bar-track"><i style={{ width: `${a.allocPct}%`, background: a.ziScore >= 68 ? "#4ade80" : "#60a5fa" }} /></div>
                        <span className="val">{a.allocPct}%</span>
                      </div>
                    ))}
                  </div>
                  <div className="an-table-wrap">
                    <table className="tbl" style={{ fontSize: 11 }}>
                      <thead><tr><th style={{ textAlign: "left" }}>Ticker</th><th>Strike</th><th>DTE</th><th>Zi</th><th>MC PoP</th><th>Qty</th><th>Cost</th><th>E[Return]</th><th>Sharpe</th></tr></thead>
                      <tbody data-testid="optimizer-rows">
                        {optimizer.allocations.map((a, i) => (
                          <tr key={i}>
                            <td style={{ textAlign: "left", fontWeight: 650 }}>{a.ticker}</td>
                            <td>${a.strike}</td>
                            <td style={{ color: a.dte <= 7 ? "#f87171" : a.dte <= 21 ? "#fbbf24" : "var(--muted)" }}>{a.dte != null ? `${a.dte}d` : "—"}</td>
                            <td style={{ fontWeight: 700, color: a.ziScore >= 68 ? "var(--green)" : "var(--blue)" }}>{a.ziScore}</td>
                            <td>{a.mcPoP}%</td>
                            <td style={{ fontWeight: 650 }}>{a.quantity}</td>
                            <td>{fmt$(a.allocCost)}</td>
                            <td className={a.expectedReturn >= 0 ? "pos" : "neg"}>{a.expectedReturn >= 0 ? "+" : ""}{fmt$(a.expectedReturn)}</td>
                            <td>{a.sharpe}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : <div style={{ color: "var(--muted)", fontSize: 12 }}>{optimizer.message || "No suitable allocations found. Run autopilot scans to populate candidate pool."}</div>}
            </div>
          )}
        </div>
      )}

      {view === "backtest" && (
        <div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 12 }}>Test Zi Score 5.1 against historical data — regime detection, IV analysis, Monte Carlo, Greeks, entry at threshold, exit at profit target / stop loss / hold period.</div>
          <div className="an-stat-grid" style={{ marginBottom: 14, alignItems: "end" }}>
            <div><div className="m-lbl">Symbol</div><input className="inp" value={btSymbol} onChange={e => setBtSymbol(e.target.value.toUpperCase())} /></div>
            <div><div className="m-lbl">Zi Threshold</div><input className="inp" type="number" value={btThreshold} onChange={e => setBtThreshold(Number(e.target.value))} /></div>
            <div><div className="m-lbl">Hold Days</div><input className="inp" type="number" value={btHold} onChange={e => setBtHold(Number(e.target.value))} /></div>
            <div><div className="m-lbl" title="Each trade risks 2% of this per contract — too small relative to the underlying's option price means the backtest can never afford a single contract.">Account Size ($)</div><input className="inp" type="number" step={1000} value={btAccountSize} onChange={e => setBtAccountSize(Number(e.target.value))} data-testid="bt-account-size" /></div>
          </div>
          <button className="an-btn primary" style={{ marginBottom: 16 }} onClick={runBacktest} disabled={btLoading} data-testid="run-backtest">{btLoading ? "Running..." : "Run Backtest"}</button>

          {btResult?.error && <div className="an-card" style={{ color: "var(--red)" }}>{btResult.error}</div>}

          {btResult?.summary && (
            <>
              <div className="an-stat-grid" style={{ marginBottom: 8 }}>
                <div className="an-stat-chip"><div className="l">Win Rate</div><div className="v" style={{ color: btResult.summary.winRate >= 50 ? "var(--green)" : "var(--red)" }}>{btResult.summary.winRate}%</div></div>
                <div className="an-stat-chip"><div className="l">Total P&L</div><div className="v" style={{ color: btResult.summary.totalPnl >= 0 ? "var(--green)" : "var(--red)" }}>{fmt$(btResult.summary.totalPnl)}</div></div>
                <div className="an-stat-chip"><div className="l">Return</div><div className="v" style={{ color: btResult.summary.returnPct >= 0 ? "var(--green)" : "var(--red)" }}>{fmtPct(btResult.summary.returnPct)}</div></div>
                <div className="an-stat-chip"><div className="l">Sharpe</div><div className="v">{btResult.summary.sharpe}</div></div>
              </div>
              <div className="an-stat-grid" style={{ marginBottom: 16 }}>
                <div className="an-stat-chip"><div className="l">Total Trades</div><div className="v">{btResult.summary.totalTrades}</div></div>
                <div className="an-stat-chip"><div className="l">Wins / Losses</div><div className="v"><span style={{ color: "var(--green)" }}>{btResult.summary.wins}</span> / <span style={{ color: "var(--red)" }}>{btResult.summary.losses}</span></div></div>
                <div className="an-stat-chip"><div className="l">Max Drawdown</div><div className="v" style={{ color: "var(--red)" }}>{fmtPct(btResult.summary.maxDrawdown)}</div></div>
                <div className="an-stat-chip"><div className="l">Final Equity</div><div className="v">{fmt$(btResult.summary.finalEquity)}</div></div>
              </div>

              {btChart && (
                <>
                  <div className="an-side-title" style={{ marginBottom: 8 }}>Equity Curve</div>
                  <div className="an-chart-box" style={{ marginBottom: 16 }}>
                    <Line data={btChart} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { display: false }, y: { ticks: { color: "var(--dim)", font: { size: 9 }, callback: v => "$" + v.toLocaleString() }, grid: { color: "var(--divider)" } } } }} />
                  </div>
                </>
              )}

              {Object.keys(btResult.summary.byClass || {}).length > 0 && (
                <>
                  <div className="an-side-title" style={{ marginBottom: 8 }}>Win Rate by Zi Classification</div>
                  <div className="an-table-wrap" style={{ marginBottom: 16 }}>
                    <table className="tbl">
                      <thead><tr><th style={{ textAlign: "left" }}>Zi Class</th><th>Trades</th><th>Win Rate</th><th>P&L</th></tr></thead>
                      <tbody>{Object.entries(btResult.summary.byClass).map(([cls, d]) => (
                        <tr key={cls}>
                          <td style={{ textAlign: "left", color: cls === "ULTRA_GEM" ? "var(--green)" : cls === "GEM" ? "var(--blue)" : "var(--yellow)" }}>{cls}</td>
                          <td>{d.trades}</td><td className={d.winRate >= 60 ? "pos" : "neg"}>{d.winRate}%</td><td className={d.pnl >= 0 ? "pos" : "neg"}>{fmt$(d.pnl)}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </>
              )}

              {btResult.trades?.length > 0 && (
                <>
                  <div className="an-side-title" style={{ marginBottom: 8 }}>Trade Log ({btResult.trades.length} trades)</div>
                  <div className="an-table-wrap" style={{ maxHeight: 300, overflowY: "auto" }}>
                    <table className="tbl" style={{ fontSize: 10 }}>
                      <thead><tr><th style={{ textAlign: "left" }}>Date</th><th>Strike</th><th>Zi</th><th>Class</th><th>MC PoP</th><th>Entry</th><th>Exit</th><th>Days</th><th>P&L</th><th>Reason</th></tr></thead>
                      <tbody>{btResult.trades.map((t, i) => (
                        <tr key={i}>
                          <td style={{ textAlign: "left" }}>{t.entry_date}</td>
                          <td>${t.strike}</td>
                          <td style={{ fontWeight: 650 }}>{t.zi_score}</td>
                          <td style={{ color: t.zi_class === "ULTRA_GEM" ? "var(--green)" : t.zi_class === "GEM" ? "var(--blue)" : "var(--yellow)" }}>{t.zi_class}</td>
                          <td>{fmtPct(t.mc_pop * 100)}</td>
                          <td>${fmtN(t.entry_price, 2)}</td>
                          <td>${fmtN(t.exit_price, 2)}</td>
                          <td>{t.days_held}</td>
                          <td className={t.won ? "pos" : "neg"}>{t.pnl_pct >= 0 ? "+" : ""}{fmtN(t.pnl_pct, 1)}%</td>
                          <td style={{ color: "var(--dim)" }}>{t.exit_reason}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Alerts ──────────────────────────────────────────────────────────────────
function AlertsSection({ alertRules, setAlertRules, newRule, setNewRule }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 12 }}>Set rules like "Alert when SPY Zi Score &gt; 85" — triggers automatically and appears in Notification Center.</div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 14, padding: 12, background: "var(--layer2)", borderRadius: 10 }}>
        <div><div className="m-lbl">Ticker</div><input className="inp" value={newRule.ticker} onChange={e => setNewRule(p => ({ ...p, ticker: e.target.value.toUpperCase() }))} placeholder="SPY" style={{ width: 80 }} data-testid="alert-ticker" /></div>
        <div><div className="m-lbl">Metric</div>
          <select className="inp" value={newRule.metric} onChange={e => setNewRule(p => ({ ...p, metric: e.target.value }))} style={{ width: 100 }} data-testid="alert-metric">
            <option value="ziScore">Zi Score</option><option value="price">Price</option><option value="iv">IV %</option><option value="teaScore">Tea Score</option><option value="mcPoP">MC PoP %</option>
          </select>
        </div>
        <div><div className="m-lbl">When</div>
          <select className="inp" value={newRule.operator} onChange={e => setNewRule(p => ({ ...p, operator: e.target.value }))} style={{ width: 60 }} data-testid="alert-operator">
            <option value=">">&gt;</option><option value="<">&lt;</option><option value=">=">&gt;=</option><option value="<=">&lt;=</option>
          </select>
        </div>
        <div><div className="m-lbl">Value</div><input className="inp" type="number" value={newRule.value} onChange={e => setNewRule(p => ({ ...p, value: Number(e.target.value) }))} style={{ width: 80 }} /></div>
        <div><div className="m-lbl">Label</div><input className="inp" value={newRule.label} onChange={e => setNewRule(p => ({ ...p, label: e.target.value }))} placeholder="Optional note" style={{ width: 130 }} /></div>
        <button className="an-btn primary" onClick={async () => {
          if (!newRule.ticker) return;
          try {
            const r = await axios.post(`${API}/alerts/rules`, newRule);
            setAlertRules(prev => [...prev, r.data]);
            setNewRule({ ticker: "", metric: "ziScore", operator: ">", value: 85, label: "" });
          } catch {}
        }} data-testid="create-alert">Create</button>
      </div>
      {alertRules.length > 0 ? (
        <div className="an-table-wrap">
          <table className="tbl" style={{ fontSize: 11 }}>
            <thead><tr><th style={{ textAlign: "left" }}>Ticker</th><th>Metric</th><th>Condition</th><th>Status</th><th>Label</th><th>Created</th><th></th></tr></thead>
            <tbody data-testid="alert-rules-rows">
              {alertRules.map((r, i) => (
                <tr key={i} style={{ background: r.triggered ? "rgba(74,222,128,0.04)" : "transparent" }}>
                  <td style={{ textAlign: "left", fontWeight: 650 }}>{r.ticker}</td>
                  <td>{r.metric}</td>
                  <td style={{ fontFamily: "'JetBrains Mono', monospace" }}>{r.operator} {r.value}</td>
                  <td><span className={`tag ${r.triggered ? "t-bull" : "t-neu"}`} style={{ fontSize: 8 }}>{r.triggered ? "TRIGGERED" : "ACTIVE"}</span></td>
                  <td style={{ color: "var(--muted)" }}>{r.label || "—"}</td>
                  <td style={{ fontSize: 9, color: "var(--dim)" }}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ""}</td>
                  <td style={{ display: "flex", gap: 4 }}>
                    {r.triggered && (
                      <button className="an-btn ghost" style={{ fontSize: 9, padding: "3px 8px" }} onClick={async () => {
                        try {
                          await axios.post(`${API}/alerts/rules/${r.rule_id}/reset`);
                          setAlertRules(prev => prev.map(a => a.rule_id === r.rule_id ? { ...a, triggered: false, triggeredAt: null, currentValue: null } : a));
                        } catch {}
                      }}>Reset</button>
                    )}
                    <button className="an-btn subtle" style={{ fontSize: 9, padding: "3px 8px" }} onClick={async () => {
                      await axios.delete(`${API}/alerts/rules/${r.rule_id}`);
                      setAlertRules(prev => prev.filter(a => a.rule_id !== r.rule_id));
                    }}>Del</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <div style={{ color: "var(--muted)", fontSize: 12, padding: 16 }}>No alert rules yet. Create one above to get notified when conditions are met.</div>}
      <div style={{ marginTop: 12 }}>
        <button className="an-btn ghost" style={{ fontSize: 10 }} onClick={async () => {
          try { const r = await axios.post(`${API}/alerts/check`); if (r.data.triggered?.length) setAlertRules(prev => prev.map(a => r.data.triggered.find(t => t.rule_id === a.rule_id) || a)); } catch {}
        }} data-testid="check-alerts">Check Alerts Now</button>
      </div>
    </div>
  );
}

// ─── Analytics Tab (root) ───────────────────────────────────────────────────
export default function AnalyticsTab({ omegaDispatch, initialSubTab, onSubTabConsumed } = {}) {
  const [activeId, setActiveId] = useState("performance");
  const [perfView, setPerfView] = useState("overview");
  const [signalsView, setSignalsView] = useState("leaderboard");
  const [logView, setLogView] = useState("journal");
  const [labView, setLabView] = useState("optimizer");

  useEffect(() => {
    if (initialSubTab) {
      const mapped = LEGACY_SUBTAB_MAP[initialSubTab];
      if (mapped) {
        setActiveId(mapped[0]);
        if (mapped[0] === "performance" && mapped[1]) setPerfView(mapped[1]);
        if (mapped[0] === "signals" && mapped[1]) setSignalsView(mapped[1]);
        if (mapped[0] === "tradelog" && mapped[1]) setLogView(mapped[1]);
        if (mapped[0] === "strategy" && mapped[1]) setLabView(mapped[1]);
      }
      if (onSubTabConsumed) onSubTabConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSubTab]);

  const [analytics, setAnalytics] = useState(null);
  const [priors, setPriors] = useState({});
  const [apPerf, setApPerf] = useState(null);
  const [attribution, setAttribution] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbSort, setLbSort] = useState("ziScore");
  const [confHistory, setConfHistory] = useState([]);
  const [confFilter, setConfFilter] = useState("all");
  const [journal, setJournal] = useState(null);
  const [alertRules, setAlertRules] = useState([]);
  const [newRule, setNewRule] = useState({ ticker: "", metric: "ziScore", operator: ">", value: 85, label: "" });

  const [optBudget, setOptBudget] = useState(0);
  const [optRisk, setOptRisk] = useState("moderate");
  const [optLoading, setOptLoading] = useState(false);
  const [optimizer, setOptimizer] = useState(null);

  const [btSymbol, setBtSymbol] = useState("SPY");
  const [btThreshold, setBtThreshold] = useState(55);
  const [btHold, setBtHold] = useState(5);
  const [btAccountSize, setBtAccountSize] = useState(10000);
  const [btResult, setBtResult] = useState(null);
  const [btLoading, setBtLoading] = useState(false);
  const [btChart, setBtChart] = useState(null);

  const reloadJournal = () => axios.get(`${API}/journal/enhanced`).then(r => setJournal(r.data)).catch(() => {});

  useEffect(() => {
    Promise.allSettled([
      axios.get(`${API}/zi/analytics`),
      axios.get(`${API}/zi/priors`),
      axios.get(`${API}/autopilot/performance`),
      axios.get(`${API}/autopilot/attribution`),
      axios.get(`${API}/zi/leaderboard`),
      axios.get(`${API}/zi/confidence-history`),
    ]).then(([aRes, pRes, apRes, attrRes, lbRes, chRes]) => {
      if (aRes.status === "fulfilled") setAnalytics(aRes.value.data);
      if (pRes.status === "fulfilled") setPriors(pRes.value.data);
      if (apRes.status === "fulfilled") setApPerf(apRes.value.data);
      if (attrRes.status === "fulfilled") setAttribution(attrRes.value.data);
      if (lbRes.status === "fulfilled") {
        const raw = lbRes.value.data?.leaderboard || [];
        setLeaderboard(raw.sort((a, b) => b.ziScore - a.ziScore));
      }
      if (chRes.status === "fulfilled") setConfHistory(chRes.value.data?.history || []);
    });
    reloadJournal();
    axios.get(`${API}/alerts/rules`).then(r => setAlertRules(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    fastGetAsync(`${API}/daily-pnl`).then(r => {
      const bp = r.data?.buyingPower;
      if (bp > 0) setOptBudget(prev => (prev === 0 ? Math.floor(bp) : prev));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runOptimizer = async () => {
    setOptLoading(true);
    try { const r = await axios.post(`${API}/portfolio/optimize`, { budget: optBudget, riskTolerance: optRisk }); setOptimizer(r.data); } catch {}
    setOptLoading(false);
  };

  const runBacktest = async () => {
    setBtLoading(true);
    setBtResult(null);
    setBtChart(null);
    try {
      const r = await axios.post(`${API}/backtest/run`, {
        symbol: btSymbol.toUpperCase(), days: 365, ziThreshold: btThreshold,
        holdDays: btHold, stopLoss: 50, profitTarget: 50, accountSize: btAccountSize,
      });
      setBtResult(r.data);
      if (r.data?.equityCurve?.length) {
        setBtChart({
          labels: r.data.equityCurve.map((_, i) => i),
          datasets: [{ label: "Equity", data: r.data.equityCurve, borderColor: "#60a5fa", backgroundColor: "rgba(96,165,250,0.08)", fill: true, pointRadius: 0, borderWidth: 1.5, tension: 0.3 }],
        });
      }
    } catch (e) {
      setBtResult({ error: e.response?.data?.detail || "Backtest failed" });
    } finally { setBtLoading(false); }
  };

  const SECTIONS = [
    { id: "performance", label: "Performance", icon: "chart" },
    { id: "signals",     label: "Signals",     icon: "target" },
    { id: "tradelog",    label: "Trade Log",   icon: "list" },
    { id: "strategy",    label: "Strategy Lab", icon: "layers" },
    { id: "alerts",      label: "Alerts",      icon: "bell" },
    { id: "enhance",     label: "Enhance",     icon: "sparkle" },
  ];

  const rootAccent = SECTION_ACCENT[activeId] || SECTION_ACCENT.performance;

  return (
    <div className="an-root" style={{ "--accent": rootAccent.accent, "--accent-bg": rootAccent.bg }} data-testid="analytics-tab">
      <div className="an-banner">
        <div>
          <div className="an-banner-title">
            <Icon name="chart" />
            <span>Analytics</span>
            <span className="tag t-bull" style={{ fontSize: 8 }}>Live</span>
          </div>
          <div className="an-banner-sub">Performance, signals, and strategy tools — powered by real trade history.</div>
        </div>
        <div className="an-banner-stats">
          <div className="an-banner-stat"><div className="v" style={{ color: (analytics?.winRate || 0) >= 50 ? "var(--green)" : "var(--red)" }}>{analytics?.winRate || 0}%</div><div className="l">Win Rate</div></div>
          <div className="an-banner-stat"><div className="v">{analytics?.totalTrades || 0}</div><div className="l">Trades</div></div>
          <div className="an-banner-stat"><div className="v" style={{ color: (analytics?.totalPnl || 0) >= 0 ? "var(--green)" : "var(--red)" }}>{fmt$(analytics?.totalPnl)}</div><div className="l">Total P&L</div></div>
        </div>
      </div>

      <div className="an-jump-strip">
        {SECTIONS.map(s => (
          <button key={s.id} className={activeId === s.id ? "on" : ""} onClick={() => setActiveId(s.id)} data-testid={`an-jump-${s.id}`}>
            <Icon name={s.icon} />{s.label}
          </button>
        ))}
      </div>

      <Section id="performance" activeId={activeId} title="Performance & Attribution" icon="chart" sub="Headline stats, signal-vector edge, and P&L breakdown">
        <PerformanceSection view={perfView} setView={setPerfView} analytics={analytics} priors={priors} apPerf={apPerf} attribution={attribution} />
      </Section>

      <Section id="signals" activeId={activeId} title="Signals" icon="target" sub="Ranked candidates and per-setup confidence evolution">
        <SignalsSection view={signalsView} setView={setSignalsView} leaderboard={leaderboard} lbSort={lbSort} setLbSort={setLbSort} setLeaderboard={setLeaderboard} confHistory={confHistory} confFilter={confFilter} setConfFilter={setConfFilter} />
      </Section>

      <Section id="tradelog" activeId={activeId} title="Trade Log" icon="list" sub="AI decision accuracy and every trade across sources">
        <TradeLogSection view={logView} setView={setLogView} journal={journal} reloadJournal={reloadJournal} />
      </Section>

      <Section id="strategy" activeId={activeId} title="Strategy Lab" icon="layers" sub="Allocate a budget or replay Zi Score against history">
        <StrategyLabSection
          view={labView} setView={setLabView}
          optBudget={optBudget} setOptBudget={setOptBudget} optRisk={optRisk} setOptRisk={setOptRisk} optLoading={optLoading} runOptimizer={runOptimizer} optimizer={optimizer}
          btSymbol={btSymbol} setBtSymbol={setBtSymbol} btThreshold={btThreshold} setBtThreshold={setBtThreshold} btHold={btHold} setBtHold={setBtHold} btAccountSize={btAccountSize} setBtAccountSize={setBtAccountSize}
          btLoading={btLoading} runBacktest={runBacktest} btResult={btResult} btChart={btChart}
        />
      </Section>

      <Section id="alerts" activeId={activeId} title="Alerts" icon="bell" sub="Custom rules that trigger into the Notification Center">
        <AlertsSection alertRules={alertRules} setAlertRules={setAlertRules} newRule={newRule} setNewRule={setNewRule} />
      </Section>

      <Section id="enhance" activeId={activeId} title="Enhance" icon="sparkle" sub="Live, account-aware suggestions from Omega">
        <OmegaEnhancementsCard variant="full" dispatch={omegaDispatch} />
      </Section>
    </div>
  );
}
